/**
 * React Query hooks for Standards Compliance feature.
 *
 * These hooks provide data fetching, caching, and mutation capabilities
 * for the OPA-based multi-framework compliance evaluation.
 */

import { useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type {
  AssignStandardRequest,
  UpdateTenantStandardRequest,
  TenantStandardAssignment,
  CreateWaiverRequest,
  UpdateWaiverRequest,
  ViolationFilters,
  UpdateStandardConfigRequest,
  ComplianceDashboardData,
  FrameworkScoreDisplay,
  ComplianceViolation,
  FrameworkScore,
  WorkloadCollectionHealth,
} from '@/types'
import {
  aggregateViolationsByWorkload,
  type M365Workload,
} from '@/types'
import { refMatchesFramework } from '@/lib/frameworkUtils'

// Query key factory for consistent cache key management
export const complianceKeys = {
  all: ['compliance'] as const,
  standards: () => [...complianceKeys.all, 'standards'] as const,
  standard: (id: string) => [...complianceKeys.standards(), id] as const,
  standardPolicies: (standardId: string) => [...complianceKeys.standard(standardId), 'policies'] as const,
  tenantStandards: (tenantId: string) => [...complianceKeys.all, 'tenant', tenantId, 'standards'] as const,
  scores: (tenantId: string) => [...complianceKeys.all, 'tenant', tenantId, 'scores'] as const,
  scoreHistory: (tenantId: string, days?: number) => [...complianceKeys.scores(tenantId), 'history', days] as const,
  scoreEvents: (tenantId: string, limit?: number) => [...complianceKeys.scores(tenantId), 'events', limit] as const,
  controlChanges: (tenantId: string, limit?: number) =>
    [...complianceKeys.scores(tenantId), 'control-changes', limit] as const,
  violations: (tenantId: string, filters?: ViolationFilters) => [...complianceKeys.all, 'tenant', tenantId, 'violations', filters] as const,
  violation: (tenantId: string, violationId: string) => [...complianceKeys.all, 'tenant', tenantId, 'violation', violationId] as const,
  evaluations: (tenantId: string) => [...complianceKeys.all, 'tenant', tenantId, 'evaluations'] as const,
  evaluation: (tenantId: string, evaluationId: string) => [...complianceKeys.evaluations(tenantId), evaluationId] as const,
  waivers: (tenantId: string) => [...complianceKeys.all, 'tenant', tenantId, 'waivers'] as const,
  // Note: when called WITHOUT the options arg the returned key is a prefix that
  // matches both starred-only and include-unstarred variants. Mutations should
  // invalidate via this prefix so both variants refetch together.
  violationSummary: (tenantId: string | undefined, options?: { includeUnstarredFrameworks?: boolean }) =>
    options === undefined
      ? [...complianceKeys.all, 'tenant', tenantId || '', 'violations', 'summary'] as const
      : [...complianceKeys.all, 'tenant', tenantId || '', 'violations', 'summary', options.includeUnstarredFrameworks ? 'all' : 'starred'] as const,
  opaHealth: () => [...complianceKeys.all, 'opa', 'health'] as const,
}

// =============================================================================
// Query Hooks - Data Fetching
// =============================================================================

/**
 * Fetch all available compliance standards/frameworks
 */
export function useComplianceStandards() {
  return useQuery({
    queryKey: complianceKeys.standards(),
    queryFn: () => api.getComplianceStandards(),
    staleTime: 5 * 60 * 1000, // Standards rarely change, cache for 5 minutes
  })
}

/**
 * Fetch a specific compliance standard by ID
 */
export function useComplianceStandard(standardId: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: complianceKeys.standard(standardId),
    queryFn: () => api.getComplianceStandard(standardId),
    enabled: !!standardId && (options?.enabled ?? true),
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Fetch policies within a standard
 */
export function useStandardPolicies(standardId: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: complianceKeys.standardPolicies(standardId),
    queryFn: () => api.getStandardPolicies(standardId),
    enabled: !!standardId && (options?.enabled ?? true),
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Fetch standards assigned to a tenant (with weights and enabled status)
 */
export function useTenantStandards(tenantId: string | undefined) {
  return useQuery({
    queryKey: complianceKeys.tenantStandards(tenantId || ''),
    queryFn: () => api.getTenantStandards(tenantId!),
    enabled: !!tenantId,
    staleTime: 1 * 60 * 1000, // 1 minute - assignments can change
  })
}

/**
 * Fetch current compliance scores for a tenant
 */
export function useComplianceScores(tenantId: string | undefined) {
  return useQuery({
    queryKey: complianceKeys.scores(tenantId || ''),
    queryFn: () => api.getComplianceScores(tenantId!),
    enabled: !!tenantId,
    staleTime: 30 * 1000, // 30 seconds - scores may update after evaluation
    refetchOnWindowFocus: true,
  })
}

/**
 * Fetch compliance score history for trend charts
 */
export function useComplianceScoreHistory(tenantId: string | undefined, days: number = 90) {
  return useQuery({
    queryKey: complianceKeys.scoreHistory(tenantId || '', days),
    queryFn: () => api.getComplianceScoreHistory(tenantId!, days),
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000, // 5 minutes - historical data changes slowly
  })
}

/**
 * Fetch raw per-evaluation score events for a tenant (no day-bucket collapse).
 * One row per evaluation event with attribution and a server-computed delta
 * vs the immediately prior event. 1-minute staleTime mirrors
 * useComplianceEvaluations because the list refreshes per-eval, not per-day.
 */
export function useComplianceScoreEvents(tenantId: string | undefined, limit: number = 20) {
  return useQuery({
    queryKey: complianceKeys.scoreEvents(tenantId || '', limit),
    queryFn: () => api.getComplianceScoreEvents(tenantId!, limit),
    enabled: !!tenantId,
    staleTime: 1 * 60 * 1000,
  })
}

/**
 * Fetch per-control change feed for a tenant — one row per policy flip
 * (Pass↔Fail, waiver applied, waiver lapsed) since the prior evaluation.
 * Each row carries the signed Guardian Score delta attributable to that flip.
 * Backend orders newest-first; 1-minute staleTime mirrors
 * useComplianceScoreEvents because the list refreshes per-eval, not per-day.
 */
export function useControlChanges(tenantId: string | undefined, limit: number = 20) {
  return useQuery({
    queryKey: complianceKeys.controlChanges(tenantId || '', limit),
    queryFn: () => api.getControlChanges(tenantId!, limit),
    enabled: !!tenantId,
    staleTime: 1 * 60 * 1000,
  })
}

/**
 * Fetch violations for a tenant with optional filtering
 */
export function useComplianceViolations(
  tenantId: string | undefined,
  filters?: ViolationFilters
) {
  return useQuery({
    queryKey: complianceKeys.violations(tenantId || '', filters),
    queryFn: () => api.getComplianceViolations(tenantId!, filters),
    enabled: !!tenantId,
    staleTime: 30 * 1000, // 30 seconds
    refetchOnWindowFocus: true,
  })
}

/**
 * Fetch a specific violation
 */
export function useComplianceViolation(
  tenantId: string | undefined,
  violationId: string | undefined,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: complianceKeys.violation(tenantId || '', violationId || ''),
    queryFn: () => api.getComplianceViolation(tenantId!, violationId!),
    enabled: !!tenantId && !!violationId && (options?.enabled ?? true),
    staleTime: 30 * 1000,
  })
}

/**
 * Fetch evaluations for a tenant
 */
export function useComplianceEvaluations(tenantId: string | undefined) {
  return useQuery({
    queryKey: complianceKeys.evaluations(tenantId || ''),
    queryFn: () => api.getComplianceEvaluations(tenantId!),
    enabled: !!tenantId,
    staleTime: 1 * 60 * 1000, // 1 minute
  })
}

/**
 * Fetch a specific evaluation
 */
export function useComplianceEvaluation(
  tenantId: string | undefined,
  evaluationId: string | undefined,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: complianceKeys.evaluation(tenantId || '', evaluationId || ''),
    queryFn: () => api.getComplianceEvaluation(tenantId!, evaluationId!),
    enabled: !!tenantId && !!evaluationId && (options?.enabled ?? true),
    staleTime: 1 * 60 * 1000,
  })
}

/**
 * Fetch all waivers for a tenant
 */
export function useComplianceWaivers(tenantId: string | undefined) {
  return useQuery({
    queryKey: complianceKeys.waivers(tenantId || ''),
    queryFn: () => api.getComplianceWaivers(tenantId!),
    enabled: !!tenantId,
    staleTime: 1 * 60 * 1000,
  })
}

/**
 * Fetch OPA service health status
 */
export function useOpaHealth() {
  return useQuery({
    queryKey: complianceKeys.opaHealth(),
    queryFn: () => api.getOpaHealth(),
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: 60 * 1000, // Refresh every minute
  })
}

/**
 * Fetch violation summary (counts, breakdowns, health) from the dedicated summary endpoint.
 * This replaces fetching all violations and counting client-side.
 *
 * @param options.includeUnstarredFrameworks When true, the server is asked to
 *   include findings from unstarred frameworks (matches the violations list
 *   endpoint's `onlyStarred=false` default behavior). Mirrors the option on
 *   useComplianceDashboard so the Violations page toggle drives both queries.
 */
export function useViolationSummary(
  tenantId: string | undefined,
  options?: { includeUnstarredFrameworks?: boolean }
) {
  return useQuery({
    queryKey: complianceKeys.violationSummary(tenantId, options),
    queryFn: () => api.getViolationSummary(
      tenantId!,
      options?.includeUnstarredFrameworks ? { onlyStarred: false } : undefined
    ),
    enabled: !!tenantId,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
  })
}

// =============================================================================
// Mutation Hooks - Data Modifications
// =============================================================================

/**
 * Trigger compliance evaluation for a tenant
 */
export function useTriggerEvaluation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      tenantId,
      options
    }: {
      tenantId: string;
      options?: { standardIds?: string[]; forceRefresh?: boolean }
    }) => api.triggerComplianceEvaluation(tenantId, options),
    onSuccess: (_data, variables) => {
      // history/events/control-changes ride on the scores() prefix, but
      // invalidate them explicitly so this isn't load-bearing on the
      // key-factory shape.
      queryClient.invalidateQueries({ queryKey: complianceKeys.scores(variables.tenantId) })
      queryClient.invalidateQueries({
        queryKey: [...complianceKeys.scores(variables.tenantId), 'history'],
      })
      queryClient.invalidateQueries({
        queryKey: [...complianceKeys.scores(variables.tenantId), 'events'],
      })
      // Explicit per-feed invalidation: redundant with the scores() prefix
      // above, but spelled out so a future refactor of the key factory can't
      // silently leave the "Recent control moves" feed stale. Use the prefix
      // array (no trailing `limit`) so every cached page size refetches —
      // matches the existing `events` invalidation pattern above.
      queryClient.invalidateQueries({
        queryKey: [...complianceKeys.scores(variables.tenantId), 'control-changes'],
      })
      queryClient.invalidateQueries({ queryKey: complianceKeys.violations(variables.tenantId) })
      queryClient.invalidateQueries({ queryKey: complianceKeys.violationSummary(variables.tenantId) })
      queryClient.invalidateQueries({ queryKey: complianceKeys.evaluations(variables.tenantId) })
    },
  })
}

/**
 * Assign a standard to a tenant
 */
export function useAssignTenantStandard() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      tenantId,
      data
    }: {
      tenantId: string;
      data: AssignStandardRequest
    }) => api.assignTenantStandard(tenantId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: complianceKeys.tenantStandards(variables.tenantId) })
    },
  })
}

/**
 * Update tenant standard configuration
 */
export function useUpdateTenantStandard() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      tenantId,
      standardId,
      data
    }: {
      tenantId: string;
      standardId: string;
      data: UpdateTenantStandardRequest
    }) => api.updateTenantStandard(tenantId, standardId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: complianceKeys.tenantStandards(variables.tenantId) })
      // Also invalidate scores as weights may have changed
      queryClient.invalidateQueries({ queryKey: complianceKeys.scores(variables.tenantId) })
    },
  })
}

/**
 * Bulk update tenant standard configurations
 */
export function useBulkUpdateTenantStandards() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      tenantId,
      updates
    }: {
      tenantId: string;
      updates: UpdateStandardConfigRequest[]
    }) => api.bulkUpdateTenantStandards(
      tenantId,
      updates.map(u => ({
        standardId: u.standardId,
        isEnabled: u.isEnabled,
        weight: u.weight,
        includeInGuardianScore: u.includeInGuardianScore
      }))
    ),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: complianceKeys.tenantStandards(variables.tenantId) })
      queryClient.invalidateQueries({ queryKey: complianceKeys.scores(variables.tenantId) })
    },
  })
}

/**
 * Create a waiver for a policy
 */
export function useCreateWaiver() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      tenantId,
      data
    }: {
      tenantId: string;
      data: CreateWaiverRequest
    }) => api.createComplianceWaiver(tenantId, data),
    onSuccess: (_, variables) => {
      // Invalidate all compliance data - backend now updates status + scores immediately
      queryClient.invalidateQueries({ queryKey: complianceKeys.waivers(variables.tenantId) })
      queryClient.invalidateQueries({ queryKey: complianceKeys.violations(variables.tenantId) })
      queryClient.invalidateQueries({ queryKey: complianceKeys.violationSummary(variables.tenantId) })
      queryClient.invalidateQueries({ queryKey: complianceKeys.scores(variables.tenantId) })
    },
  })
}

/**
 * Delete (deactivate) a waiver
 */
export function useDeleteWaiver() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      tenantId,
      waiverId
    }: {
      tenantId: string;
      waiverId: string
    }) => api.deleteComplianceWaiver(tenantId, waiverId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: complianceKeys.waivers(variables.tenantId) })
      queryClient.invalidateQueries({ queryKey: complianceKeys.violations(variables.tenantId) })
      queryClient.invalidateQueries({ queryKey: complianceKeys.violationSummary(variables.tenantId) })
      queryClient.invalidateQueries({ queryKey: complianceKeys.scores(variables.tenantId) })
    },
  })
}

/**
 * Update an existing waiver
 */
export function useUpdateWaiver() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      tenantId,
      waiverId,
      data
    }: {
      tenantId: string;
      waiverId: string;
      data: UpdateWaiverRequest
    }) => api.updateComplianceWaiver(tenantId, waiverId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: complianceKeys.waivers(variables.tenantId) })
      queryClient.invalidateQueries({ queryKey: complianceKeys.violations(variables.tenantId) })
      queryClient.invalidateQueries({ queryKey: complianceKeys.violationSummary(variables.tenantId) })
      queryClient.invalidateQueries({ queryKey: complianceKeys.scores(variables.tenantId) })
    },
  })
}

// =============================================================================
// Combined Hooks - Aggregate Multiple Queries
// =============================================================================

/**
 * Hook that fetches all compliance data for the Standards Compliance page
 * This combines multiple queries for efficient data loading
 */
export function useCompliancePageData(tenantId: string | undefined) {
  const scoresQuery = useComplianceScores(tenantId)
  const violationsQuery = useComplianceViolations(tenantId)
  const assignmentsQuery = useTenantStandards(tenantId)
  const historyQuery = useComplianceScoreHistory(tenantId)

  return {
    // Individual query results
    scores: scoresQuery,
    violations: violationsQuery,
    assignments: assignmentsQuery,
    history: historyQuery,

    // Aggregated loading state
    isLoading: scoresQuery.isLoading || violationsQuery.isLoading ||
               assignmentsQuery.isLoading || historyQuery.isLoading,

    // Aggregated error state
    isError: scoresQuery.isError || violationsQuery.isError ||
             assignmentsQuery.isError || historyQuery.isError,

    // Combined data for convenience
    summary: scoresQuery.data,
    violationItems: violationsQuery.data?.items || [],
    standardAssignments: assignmentsQuery.data || [],
    trendData: historyQuery.data || [],

    // Refetch all data
    refetchAll: () => {
      scoresQuery.refetch()
      violationsQuery.refetch()
      assignmentsQuery.refetch()
      historyQuery.refetch()
    },
  }
}

// =============================================================================
// Dashboard Data Hook - Enhanced Aggregation for New Dashboard
// =============================================================================

// Friendly display names for frameworks whose DB name is just the code
const FRAMEWORK_DISPLAY_NAMES: Record<string, string> = {
  'EIDSCA': 'Entra ID Security Config Analyzer (EIDSCA)',
}

/**
 * Transforms framework scores to display format with extended properties.
 * Adds description, icon, color, and workload breakdown for Guardian framework.
 */
function transformToDisplayFrameworks(
  frameworkScores: FrameworkScore[] | undefined,
  violations: ComplianceViolation[]
): FrameworkScoreDisplay[] {
  if (!frameworkScores) return []

  return frameworkScores.map((f): FrameworkScoreDisplay => {
    // Get violations for this framework
    const frameworkViolations = violations.filter(v => (v.frameworkReferences ?? []).some(ref => refMatchesFramework(ref, f.standardCode)))
    const openViolations = frameworkViolations.filter(v => v.status === 'Open')
    const criticalViolations = openViolations.filter(v => v.criticality === 'SHALL')

    // Check if this is the "Guardian" synthetic framework
    const isGuardian = f.standardCode === 'GUARDIAN' || f.standardCode === 'guardian'

    // Calculate workload breakdown for Guardian framework
    let workloadBreakdown: FrameworkScoreDisplay['workloadBreakdown'] = undefined
    if (isGuardian) {
      workloadBreakdown = calculateWorkloadBreakdown(violations)
    }

    // Get framework-specific styling
    const { icon, color, description } = getFrameworkMetadata(f.standardCode)

    return {
      standardId: f.standardId,
      code: f.standardCode,
      name: FRAMEWORK_DISPLAY_NAMES[f.standardCode] || f.standardName,
      score: f.weightedScore,
      passedControls: f.passedControls,
      failedControls: f.failedControls,
      totalControls: f.totalControls,
      waivedControls: f.waivedControls,
      dataUnavailableControls: f.dataUnavailableControls ?? 0,
      partialControls: f.partialControls ?? 0,
      weight: f.weight,
      includeInGuardianScore: f.includeInGuardianScore,
      lastEvaluatedAt: f.lastEvaluatedAt,
      // Extended properties
      description,
      violations: openViolations.length,
      criticalViolations: criticalViolations.length,
      isGuardian,
      workloadBreakdown,
      icon,
      color,
    }
  })
}

// Workload colors and icons for the Guardian composite score breakdown
const WORKLOAD_META: Record<M365Workload, { color: string; icon: string }> = {
  'Identity': { color: 'blue', icon: 'UserCircleIcon' },
  'Exchange': { color: 'orange', icon: 'EnvelopeIcon' },
  'Teams': { color: 'purple', icon: 'ChatBubbleLeftRightIcon' },
  'SharePoint': { color: 'teal', icon: 'FolderIcon' },
  'Defender': { color: 'red', icon: 'ShieldCheckIcon' },
  'Other': { color: 'gray', icon: 'CubeIcon' },
}

/**
 * Calculate workload breakdown for the Guardian composite score.
 * Uses aggregateViolationsByWorkload as the single source of truth,
 * then maps to the display format with colors and icons.
 */
function calculateWorkloadBreakdown(
  violations: ComplianceViolation[]
): FrameworkScoreDisplay['workloadBreakdown'] {
  const summaries = aggregateViolationsByWorkload(violations)

  return summaries.map(ws => ({
    name: ws.workload,
    violations: ws.violations,
    critical: ws.critical,
    controls: ws.controls,
    passed: Math.max(0, ws.controls - ws.violations),
    color: WORKLOAD_META[ws.workload]?.color ?? 'gray',
    icon: WORKLOAD_META[ws.workload]?.icon ?? 'CubeIcon',
  }))
}

/**
 * Get framework-specific metadata for display.
 */
function getFrameworkMetadata(code: string): {
  icon: string
  color: string
  description: string
} {
  const metadata: Record<string, { icon: string; color: string; description: string }> = {
    'GUARDIAN': {
      icon: 'ShieldCheckIcon',
      color: 'primary',
      description: 'Unified M365 security score across all frameworks',
    },
    'SCUBA': {
      icon: 'BuildingOffice2Icon',
      color: 'blue',
      description: 'CISA Secure Cloud Business Applications baseline',
    },
    'NIST_800_53': {
      icon: 'DocumentCheckIcon',
      color: 'emerald',
      description: 'NIST SP 800-53 Rev 5 Security Controls',
    },
    'NIST_CSF': {
      icon: 'CpuChipIcon',
      color: 'violet',
      description: 'NIST Cybersecurity Framework',
    },
    'MCSB': {
      icon: 'CloudIcon',
      color: 'cyan',
      description: 'Microsoft Cloud Security Benchmark',
    },
    'EIDSCA': {
      icon: 'UserCircleIcon',
      color: 'amber',
      description: 'Entra ID Security Configuration Analyzer',
    },
    'MAESTER': {
      icon: 'CommandLineIcon',
      color: 'pink',
      description: 'Community-driven Maester security tests',
    },
    'CIS': {
      icon: 'shield-check',
      color: '#6366f1',
      description: 'CIS Microsoft 365 Foundations Benchmark v5.0.0 security recommendations.',
    },
  }

  return metadata[code.toUpperCase()] || {
    icon: 'ShieldCheckIcon',
    color: 'slate',
    description: 'Compliance framework',
  }
}

/**
 * Hook that provides dashboard-ready compliance data with workload aggregation.
 * This is the main hook for the redesigned compliance dashboard.
 *
 * Returns:
 * - dashboardData: Comprehensive data structure for rendering the dashboard
 * - isLoading/isError: Loading and error states
 * - refetch: Function to refresh all data
 * - toggleIncludeInScore: Mutation to toggle framework inclusion in Guardian score
 */
export function useComplianceDashboard(
  tenantId: string | undefined,
  options?: { includeUnstarredFrameworks?: boolean }
) {
  const scoresQuery = useComplianceScores(tenantId)
  // Use the summary endpoint for accurate counts (not limited by page size).
  // Forward includeUnstarredFrameworks so the summary aggregation matches the
  // violations list when the Violations page toggle is on.
  const summaryQuery = useViolationSummary(tenantId, {
    includeUnstarredFrameworks: options?.includeUnstarredFrameworks,
  })
  // Fetch all violations for display in By Standard / By Workload tabs.
  // Default behavior: server returns starred-only (frameworks the tenant has
  // opted into the Guardian Score). Pass includeUnstarredFrameworks=true to
  // surface findings from unstarred frameworks (Violations page toggle).
  const violationsQuery = useComplianceViolations(tenantId, {
    pageSize: 500,
    ...(options?.includeUnstarredFrameworks ? { onlyStarred: false } : {}),
  })
  const assignmentsQuery = useTenantStandards(tenantId)
  const historyQuery = useComplianceScoreHistory(tenantId)

  // Calculate derived dashboard data
  const dashboardData = useMemo((): ComplianceDashboardData | null => {
    const scores = scoresQuery.data
    const violationSummary = summaryQuery.data
    const violations = violationsQuery.data?.items || []

    if (!scores) return null

    // Prefer server-provided counts from the summary endpoint
    const totalViolations = violationSummary?.open ?? violations.filter(v => v.status === 'Open').length
    const criticalViolations = violationSummary?.byCriticality?.['SHALL'] ?? violations.filter(v => v.status === 'Open' && v.criticality === 'SHALL').length
    const highViolations = violationSummary?.byCriticality?.['SHOULD'] ?? violations.filter(v => v.status === 'Open' && v.criticality === 'SHOULD').length
    const mediumViolations = violationSummary?.byCriticality?.['MAY'] ?? violations.filter(v => v.status === 'Open' && v.criticality === 'MAY').length

    // Transform framework scores with extended properties
    const frameworks = transformToDisplayFrameworks(scores.standardScores, violations)

    // Use client-side aggregation for workload summary (violations data drives the detailed view)
    const workloadSummary = aggregateViolationsByWorkload(violations)

    return {
      tenantId: scores.tenantId,
      overallScore: scores.overallScore,
      totalViolations,
      criticalViolations,
      highViolations,
      mediumViolations,
      totalControls: violationSummary?.totalControls ?? scores.totalControls,
      passedControls: scores.totalPassedControls,
      waivedControls: scores.totalWaivedControls,
      lastEvaluatedAt: scores.lastEvaluatedAt,
      frameworks,
      workloadSummary,
    }
  }, [scoresQuery.data, summaryQuery.data, violationsQuery.data])

  // Merge collection health from summary endpoint or scores endpoint
  const collectionHealth = useMemo((): WorkloadCollectionHealth[] | undefined => {
    return summaryQuery.data?.collectionHealth ?? scoresQuery.data?.collectionHealth
  }, [summaryQuery.data, scoresQuery.data])

  return {
    // Dashboard-ready data
    dashboardData,

    // Collection health data for health badges
    collectionHealth,

    // Server-side violation summary
    violationSummary: summaryQuery.data,

    // Raw query data for detailed views
    summary: scoresQuery.data,
    violations: violationsQuery.data?.items || [],
    assignments: assignmentsQuery.data || [],
    trendData: historyQuery.data || [],

    // Loading and error states
    isLoading: scoresQuery.isLoading || summaryQuery.isLoading ||
               violationsQuery.isLoading || assignmentsQuery.isLoading,
    isError: scoresQuery.isError || violationsQuery.isError ||
             assignmentsQuery.isError,

    // Refetch all data
    refetch: () => {
      scoresQuery.refetch()
      summaryQuery.refetch()
      violationsQuery.refetch()
      assignmentsQuery.refetch()
      historyQuery.refetch()
    },
  }
}

// =============================================================================
// Toggle Include In Score Mutation
// =============================================================================

/**
 * Mutation to toggle whether a framework is included in the Guardian score.
 * Provides optimistic updates for immediate UI feedback.
 */
export function useToggleIncludeInScore() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      tenantId,
      standardId,
      includeInGuardianScore,
    }: {
      tenantId: string
      standardId: string
      includeInGuardianScore: boolean
    }) => {
      // Look up current assignment to preserve isEnabled and weight
      const assignments = queryClient.getQueryData<TenantStandardAssignment[]>(
        complianceKeys.tenantStandards(tenantId)
      )
      const current = assignments?.find(a => a.standardId === standardId)

      return api.toggleStandardIncludeInScore(tenantId, standardId, {
        isEnabled: current?.isEnabled ?? true,
        weight: current?.weight ?? 50,
        includeInGuardianScore,
      })
    },

    // Optimistic update for immediate UI feedback
    onMutate: async ({ tenantId, standardId, includeInGuardianScore }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: complianceKeys.scores(tenantId) })
      await queryClient.cancelQueries({ queryKey: complianceKeys.tenantStandards(tenantId) })

      // Snapshot previous values
      const previousScores = queryClient.getQueryData(complianceKeys.scores(tenantId))
      const previousAssignments = queryClient.getQueryData(complianceKeys.tenantStandards(tenantId))

      // Optimistically update scores cache
      queryClient.setQueryData(complianceKeys.scores(tenantId), (old: any) => {
        if (!old) return old

        // Update the standard scores array
        const updatedStandardScores = old.standardScores?.map((s: any) =>
          s.standardId === standardId
            ? { ...s, includeInGuardianScore }
            : s
        )

        // Recalculate overall score based on included frameworks
        const includedScores = updatedStandardScores?.filter((s: any) => s.includeInGuardianScore) || []
        const totalWeight = includedScores.reduce((sum: number, s: any) => sum + (s.weight || 0), 0)
        const weightedSum = includedScores.reduce(
          (sum: number, s: any) => sum + (s.weightedScore * (s.weight || 0)),
          0
        )
        const newOverallScore = totalWeight > 0 ? Math.round(weightedSum / totalWeight) : 0

        return {
          ...old,
          overallScore: newOverallScore,
          standardScores: updatedStandardScores,
          frameworkScores: updatedStandardScores?.map((s: any) => ({
            standardId: s.standardId,
            code: s.standardCode,
            name: s.standardName,
            score: s.weightedScore,
            passedControls: s.passedControls,
            failedControls: s.failedControls,
            totalControls: s.totalControls,
            waivedControls: s.waivedControls,
            weight: s.weight,
            includeInGuardianScore: s.includeInGuardianScore,
            lastEvaluatedAt: s.lastEvaluatedAt,
          })),
        }
      })

      // Optimistically update assignments cache
      queryClient.setQueryData(complianceKeys.tenantStandards(tenantId), (old: any) => {
        if (!old) return old
        return old.map((a: any) =>
          a.standardId === standardId
            ? { ...a, includeInGuardianScore }
            : a
        )
      })

      // Return context for rollback
      return { previousScores, previousAssignments }
    },

    // Rollback on error
    onError: (_err, { tenantId }, context) => {
      if (context?.previousScores) {
        queryClient.setQueryData(complianceKeys.scores(tenantId), context.previousScores)
      }
      if (context?.previousAssignments) {
        queryClient.setQueryData(complianceKeys.tenantStandards(tenantId), context.previousAssignments)
      }
    },

    // Refetch after mutation settles
    onSettled: (_, __, { tenantId }) => {
      queryClient.invalidateQueries({ queryKey: complianceKeys.scores(tenantId) })
      queryClient.invalidateQueries({ queryKey: complianceKeys.tenantStandards(tenantId) })
    },
  })
}

// =============================================================================
// Unified Guardian Score Helpers
// =============================================================================

/**
 * Calculate unified Guardian Score from component values.
 * Algorithm: 30% Secure Score + 30% BPA (starred) + 20% MFA + 20% Config Compliance
 *
 * This function normalizes weights when some components are missing,
 * ensuring the score is always calculated from available data.
 *
 * @param components Object with optional score values for each component
 * @returns Calculated score (0-100) or null if no data available
 */
export function calculateUnifiedGuardianScore(components: {
  secureScore?: number | null
  bpaScore?: number | null  // Should already be filtered to starred frameworks + MCSB
  mfaAdoption?: number | null
  configCompliance?: number | null
}): number | null {
  const scores: { value: number; weight: number }[] = []

  if (components.secureScore != null) {
    scores.push({ value: components.secureScore, weight: 30 })
  }
  if (components.bpaScore != null) {
    scores.push({ value: components.bpaScore, weight: 30 })
  }
  if (components.mfaAdoption != null) {
    scores.push({ value: components.mfaAdoption, weight: 20 })
  }
  if (components.configCompliance != null) {
    scores.push({ value: components.configCompliance, weight: 20 })
  }

  if (scores.length === 0) return null

  // Normalize weights to 100% when some components are missing
  const totalWeight = scores.reduce((sum, s) => sum + s.weight, 0)
  const weightedSum = scores.reduce((sum, s) => sum + s.value * s.weight, 0)

  return Math.round(weightedSum / totalWeight)
}

/**
 * Calculate BPA score from starred frameworks only (MCSB always included).
 * Use this to get the BPA component for the Guardian Score.
 *
 * @param frameworks Array of framework scores with includeInGuardianScore flag
 * @returns Weighted average score of starred frameworks, or null
 */
export function calculateStarredBpaScore(
  frameworks: Array<{
    code: string
    score?: number
    weight?: number
    includeInGuardianScore?: boolean
  }> | undefined
): number | null {
  if (!frameworks || frameworks.length === 0) return null

  // Only include frameworks the user has explicitly opted into the Guardian Score.
  // MCSB defaults to includeInGuardianScore=true for new tenants (backend default),
  // but if the user unchecks everything, BPA drops from the Guardian Score and
  // its 30% weight redistributes across remaining components.
  const scoringFrameworks = frameworks.filter(f => f.includeInGuardianScore)

  if (scoringFrameworks.length === 0) return null

  // Weighted average using actual weights from the backend (not hardcoded defaults)
  const totalWeight = scoringFrameworks.reduce((sum, f) => sum + (f.weight || 1), 0)
  const weightedSum = scoringFrameworks.reduce(
    (sum, f) => sum + ((f.score || 0) * (f.weight || 1)),
    0
  )

  return totalWeight > 0 ? Math.round(weightedSum / totalWeight) : 0
}

/**
 * Get list of framework names included in the Guardian Score.
 * Useful for displaying transparency note showing which frameworks contribute.
 *
 * @param frameworks Array of frameworks with includeInGuardianScore flag
 * @returns Array of framework names (MCSB marked as "always")
 */
export function getIncludedFrameworkNames(
  frameworks: Array<{
    code: string
    name: string
    includeInGuardianScore?: boolean
  }> | undefined
): string[] {
  if (!frameworks) return []

  return frameworks
    .filter(f => f.includeInGuardianScore)
    .map(f => f.name)
}

// =============================================================================
// Score Trend Hook - Historical Comparison
// =============================================================================

/**
 * Trend direction for score comparison
 */
export type ScoreTrendDirection = 'up' | 'down' | 'stable'

/**
 * Result of score trend calculation
 */
export interface ScoreTrendResult {
  /** Direction of change: up, down, or stable */
  direction: ScoreTrendDirection
  /** Absolute points change (always positive) */
  change: number
  /** Percentage change (always positive) */
  percentChange: number
  /** Score from comparison period */
  previousScore: number
  /** Human-readable period description */
  period: string
}

/**
 * Hook that calculates score trends comparing current score to historical data.
 * Returns trend direction, percentage change, and comparison period.
 *
 * IMPORTANT: This hook requires tenantId for tenant isolation - it will not
 * fetch data without a valid tenant context, ensuring no cross-tenant data access.
 *
 * @param tenantId - Required tenant ID for data isolation (query disabled if undefined)
 * @param currentScore - Current score to compare against
 * @param days - Number of days to look back (default: 7)
 * @returns Object with trend data and loading state
 */
export function useScoreTrend(
  tenantId: string | undefined,
  currentScore: number | null | undefined,
  days: number = 7
): { trend: ScoreTrendResult | null; isLoading: boolean } {
  // Fetch history for comparison period plus buffer to find closest historical point
  // useComplianceScoreHistory internally validates tenantId and won't fetch without it
  const historyQuery = useComplianceScoreHistory(tenantId, days + 7)

  const trend = useMemo((): ScoreTrendResult | null => {
    // Require both current score and history data for trend calculation
    if (currentScore == null || !historyQuery.data || historyQuery.data.length === 0) {
      return null
    }

    // Calculate target date for comparison
    const targetDate = new Date()
    targetDate.setDate(targetDate.getDate() - days)

    // Sort history chronologically to find closest score to target date
    // ComplianceTrendPoint uses 'date' and 'overallScore' properties
    const sortedHistory = [...historyQuery.data].sort((a, b) =>
      new Date(a.date).getTime() - new Date(b.date).getTime()
    )

    // Find the score closest to (but not after) target date
    let previousScore: number | null = null
    for (const entry of sortedHistory) {
      const entryDate = new Date(entry.date)
      if (entryDate <= targetDate) {
        previousScore = entry.overallScore
      } else {
        break
      }
    }

    // Fall back to oldest available score if no score from target date period
    if (previousScore == null && sortedHistory.length > 0) {
      previousScore = sortedHistory[0].overallScore
    }

    if (previousScore == null) return null

    // Calculate change metrics
    const change = currentScore - previousScore
    const percentChange = previousScore > 0
      ? Math.round(Math.abs(change / previousScore) * 100)
      : 0

    // Determine direction with small threshold (0.5 points) to avoid noise
    const direction: ScoreTrendDirection =
      change > 0.5 ? 'up' :
      change < -0.5 ? 'down' :
      'stable'

    // Format period for human-readable display
    const period = days === 7 ? 'week' : days === 30 ? 'month' : `${days} days`

    return {
      direction,
      change: Math.abs(Math.round(change)),
      percentChange,
      previousScore,
      period,
    }
  }, [currentScore, historyQuery.data, days])

  return {
    trend,
    isLoading: historyQuery.isLoading,
  }
}
